<template>
  <b-container
    class="square-transaction-card-payment-wrapper"
  >
    <square-payments-response-modals
      :success-component="successComponent"
      :failure-component="failureComponent"
      :error-component="errorComponent"
      :debug="debugPayment"
      @closed="completeModalClose"
    />
    <square-payments-recaptcha-verify-wrapper
      v-if="recaptchaVerificationError"
      :recaptcha-error="recaptchaError"
      @closed="init"
    />
    <square-payments-debug-info
      v-if="debugPayment"
      :debug-info="debugInfo"
    />

    <div v-if="paymentInitError">
      <b-modal
        id="payment-init-error"
        v-model="showPaymentInitError"
        hide-header
        no-close-on-esc
        ok-only
        size="lg"
        static
        @ok="errorModalClosed"
      >
        <square-payments-payment-init-error
          :error="paymentInitError"
        />
      </b-modal>
    </div>

    <b-row
      v-if="!isAnonymous && !ignoreUserStoredCards"
      v-show="!loading"
      class="mb-3"
    >
      <b-col>
        <square-payments-stored-card-select
          v-model="selectedCard"
          :account="account"
          :allow-other="true"
          @error="usercardLoadingError"
          @loading="usercardSelectStatusHandler"
        >
          <template v-slot:stored-card-header>
            <slot name="stored-card-header">
              <h3>
                Select from the stored credit cards linked to your account.
              </h3>
            </slot>
          </template>
          <template v-slot:stored-card-message>
            <slot name="stored-card-message">
              Select from the stored credit cards linked to your account.
            </slot>
          </template>
          <template v-slot:enter-new-card-label>
            <slot name="enter-new-card-label">
              Enter new card
            </slot>
          </template>
        </square-payments-stored-card-select>
      </b-col>
    </b-row>

    <b-row
      v-show="showNewCardEntry && !processingComplete"
      class="default-card-source"
    >
      <b-col>
        <div
          v-show="!loading"
          class="card-entry-header"
        >
          <slot name="credit-card-header" />
        </div>
        <div
          v-show="!loading"
          class="card-entry-message"
        >
          <slot name="credit-card-message" />
        </div>
        <div
          v-show="!loading"
          id="square-card-container"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="squareFormLoaded"
      v-show="showCardSourceIDOverride"
      class="override-card-source"
    >
      <b-col>
        <div class="message">
          <p>Card Source Override is set which overrides the card entry for testing purposed.</p>

          <p>If you believe this is an error, please contact support.</p>
        </div>
        <div class="card-source">
          Override Card Source is set to <strong>{{ overrideCardSource }}</strong>
        </div>
      </b-col>
    </b-row>

    <b-row
      v-show="showStoreNewCardOption && !processing && !processingComplete"
      class="mt-3"
    >
      <b-col>
        <b-form-checkbox
          v-model="storeCreditCardSelection"
          data-cy="store-card-checkbox"
          :value="true"
          :disabled="processing"
          class="store-user-credit-card payment-option"
          :unchecked-value="false"
        >
          <slot
            name="store-credit-card-details"
          >
            Store credit card details for future use
          </slot>
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-row
      v-show="!loading && showPaymentButton"
      v-if="!processingComplete"
    >
      <b-col>
        <payment-button
          data-cy="square-payment-button"
          :disabled="processing"
          form-type="Payments"
          @click="processPayment"
        >
          <slot>
            <div class="payment-button-content">
              <span class="amount">Pay {{ displayAmount | formatCurrency(currency) }}</span>
              <div
                v-if="displaySurcharge"
                class="surcharge-message"
              >
                Amount includes {{ surchargeAmount | formatCurrency(currency) }} surcharge
              </div>
            </div>
          </slot>
        </payment-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SquarePaymentsMixin from '../mixins/square-payments'
import PaymentButton from './card-payment-button.vue'
import SquarePaymentResponseModals from '../modals/response-modals.vue'
import SquareRecaptchaVerifyWrapper from '../modals/recaptch-verify-wrapper.vue'
import SquarePaymentInitError from '../modals/content/payment-init-error.vue'
import UserStoredCardSelect from '../components/UserStoredCardSelect.vue'
import SquarePaymentsDebugInfo from '../debug/SquarePaymentsDebugInfo.vue'

export default {
  name: 'square-payments-card-payment',
  components: {
    'square-payments-response-modals': SquarePaymentResponseModals,
    'square-payments-recaptcha-verify-wrapper': SquareRecaptchaVerifyWrapper,
    'payment-button': PaymentButton,
    'square-payments-payment-init-error': SquarePaymentInitError,
    'square-payments-stored-card-select': UserStoredCardSelect,
    'square-payments-debug-info': SquarePaymentsDebugInfo
  },
  mixins: [
    SquarePaymentsMixin
  ],
  props: {
    txn: {
      type: Object,
      required: true
    },
    amount: {
      type: [Number, String],
      required: true
    },
    account: {
      type: Object,
      required: false,
      default: () => null
    },
    recaptchaError: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    successComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    failureComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    errorComponent: {
      type: [Object, Boolean],
      required: false,
      default: () => null
    },
    options: {
      type: Object,
      required: false,
      default: () => ({
        debug: false,
        displaySurcharge: false,
        removeStoreCardButton: false,
        defaultCardholderName: '',
        ignoreUserStoredCards: false,
        enableUserCardStorage: true,
        defaultUserCardStorage: true,
        requestOptions: {},
        storeUserCardDefault: false,
        paymentMethods: {}
      })
    }
  },
  data: function() {
    return {
      elements: null,
      storeCreditCardSelection: false,
      cardContainerId: '#square-card-container',
      loading: false,
      processing: false,
      selectedCard: 'new',
      squareFormLoaded: false,
      squareFormInitialized: false, // Track if Square form has been initialized
      loadingStoredCards: false,
      showPaymentButtonVal: false,
      recaptchaVerificationError: false,
      paymentRequest: null,
      cardContainerObserver: null // Intersection Observer for card container
    }
  },
  computed: {
    userid: function() {
      return this.account ? this.account.userid : '0'
    },
    txnid: function() {
      return this.txn && this.txn.txnid
    },
    showNewCardEntry: function() {
      return !!(this.selectedCard === 'new') && !this.showCardSourceIDOverride
    },
    showStoreNewCardOption: function() {
      return !this.isAnonymous && this.showNewCardEntry && !this.showCardSourceIDOverride && !this.loading && this.squareFormLoaded
    },
    ignoreUserStoredCards: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'ignoreUserStoredCards') && this.options.ignoreUserStoredCards) {
        return this.options.ignoreUserStoredCards
      }
      return false
    },
    enableUserCardStorage: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'enableUserCardStorage')) {
        return this.options.enableUserCardStorage
      }
      return true
    },
    defaultUserCardStorage: function() {
      if (Object.prototype.hasOwnProperty.call(this.options, 'defaultUserCardStorage') && this.options.enableUserCardStorage) {
        return this.options.defaultUserCardStorage
      }
      return true
    },
    showPaymentButton: function() {
      if (!this.processing && !this.loading) {
        if (this.isAnonymous) {
          return this.showPaymentButtonVal
        }
        else if (this.showNewCardEntry && this.squareFormLoaded) {
          return this.showPaymentButtonVal
        }
        else {
          return true
        }
      }
      return false
    },
    debugPayment: function() {
      try {
        if (this.testMode) {
          return !!this.options.requestOptions.debug
        }
        return false
      }
      catch (err) {
        return false
      }
    },
    displayAmount: function() {
      const amount = parseFloat(this.amount) + this.surchargeAmount
      return amount.toFixed(2)
    },
    paymentInitError: function() {
      return this.$store.state.square.paymentInitError
    },
    showPaymentInitError: {
      get: function() {
        return !!this.$store.state.square.paymentInitError
      },
      set: function(val) {
      }
    },
    simulateRecapthaResponse: function() {
      try {
        if (Object.prototype.hasOwnProperty.call(this.options.requestOptions, 'simulateRecaptchaResponse')) {
          return this.options.requestOptions.simulateRecapthaResponse
        }
      }
      catch (_err) {
        // No-Op
      }
      return false
    },
    showCardSourceIDOverride: function() {
      return !!(this.testMode && this.options.overrideSourceID && this.options.overrideSourceID.value !== 'default') && !this.loading
    },
    surchargeAmount: function() {
      if (this.paymentRequest && this.paymentRequest.surcharge > 0) {
        return this.paymentRequest.surcharge
      }
      return 0
    },
    displaySurcharge: function() {
      if (this.surchargeAmount > 0) {
        return this.options.displaySurcharge
      }
      return false
    },
    storeCreditCard: function() {
      return this.storeCreditCardSelection && this.enableUserCardStorage && this.selectedCard === 'new'
    },
    paymentIntentType: function() {
      if (this.storeCreditCard) {
        return 'CHARGE_AND_STORE' // or 'AUTHORIZE' based on your business logic
      }
      return 'CHARGE' // or 'AUTHORIZE' based on your business logic
    },
    customerInitiated: function() {
      return true
    },
    sellerKeyedIn: function() {
      return false
    },
    isPaymentFailure: function() {
      if (this.processingResult) {
        return !this.processingResult.status &&
          this.type === 'payment' &&
          this.processingResult.payment &&
          ['CANCELED', 'FAILED'].includes(this.processingResult.payment.status)
      }
      return false
    },
    verificationDetails: function() {
      return {
        amount: typeof this.txn.gross === 'number' && this.txn.gross > 0 ? this.txn.gross.toFixed(2) : '1.00',
        billingContact: Object.entries({
          givenName: this.txn.address?.billing?.firstname || null,
          familyName: this.txn.address?.billing?.lastname || null,
          email: this.txn.email || null,
          phone: this.txn.address?.shipping?.phone || null,
          addressLines: this.txn.address !== undefined
            ? [
              this.txn.address?.billing?.street1 || null,
              this.txn.address?.billing?.street2 || null
            ].filter(_ => _)
            : [],
          city: this.txn.address !== undefined ? this.txn.address?.billing?.city || null : null,
          state: this.txn.address !== undefined ? this.txn.address?.billing?.state || null : null,
          countryCode: this.txn.address !== undefined ? this.txn.address?.billing?.country.toUpperCase() || null : null,
          postalCode: this.txn.address !== undefined ? this.txn.address?.billing?.postcode || this.txn.address?.billing?.zip || this.txn.address?.billing?.postalcode || null : null
        }).reduce((acc, [key, value]) => value ? { ...acc, [key]: value } : acc, {}),
        currencyCode: (this.$store.getters['square/dflt_currency'] || 'AUD').toUpperCase(),
        intent: this.paymentIntentType,
        customerInitiated: this.customerInitiated,
        sellerKeyedIn: this.sellerKeyedIn
      }
    },
    debugInfo: function() {
      return {
        txnid: this.txnid,
        amount: this.amount,
        paymentRequestId: this.paymentRequest ? this.paymentRequest.id : '--',
        userid: this.userid,
        selectedCard: this.selectedCard,
        storeCreditCardSelection: this.storeCreditCardSelection,
        enableUserCardStorage: this.enableUserCardStorage,
        paymentIntentType: this.paymentIntentType,
        customerInitiated: this.customerInitiated,
        sellerKeyedIn: this.sellerKeyedIn,
        verificationDetails: this.verificationDetails,
        debugPayment: this.debugPayment,
        loading: this.loading,
        surchargeAmount: this.surchargeAmount
      }
    }

  },
  watch: {
    txnid: function() {
      if (!this.$store.getters.AcceleratedTransactions) {
        this.init()
      }
    },
    userid: function(val) {
      // this.$store.commit('square/STORE_USER_CARD')
      this.$store.commit('square/USERID', val)
    },
    selectedCard: function(newCard, oldCard) {
      // Only initialize Square form when user selects "Enter new card"
      if (newCard === 'new' && !this.squareFormInitialized) {
        this.initializeSquareCardForm()
        // Don't update loading state here - let initializeSquareCardForm do it after elements are ready
      }
      else if (newCard !== 'new' && this.squareFormInitialized) {
        // Clean up Square form when switching away from "new card"
        this.cleanupSquareForm()
        this.squareFormInitialized = false
        this.squareFormLoaded = false
      }
    }
  },
  mounted: function() {
    this.$store.commit('square/PROCESSING_RESULT')
    this.processingComplete = false
    this.init()
  },
  beforeDestroy() {
    // Clean up intersection observer if it exists
    if (this.cardContainerObserver) {
      this.cardContainerObserver.disconnect()
      this.cardContainerObserver = null
    }

    // Clean up Square form when component is destroyed
    this.cleanupSquareForm()
  },
  methods: {
    usercardSelectStatusHandler: function(val) {
      this.updateLoadingState({ loadingStoredCards: val })
    },
    updateLoadingState: function(options) {
      // Apply state changes immediately
      Object.entries(options).forEach(([k, v]) => {
        this[k] = v
      })

      // Always emit loading event immediately for regular updates
      this.$emit('loading', (this.loadingStoredCards || this.loading))
    },
    init: function() {
      if (this.testMode) {
        console.log('Square Payment Init stated.')
      }

      // Clean up any existing Square form before initializing
      this.cleanupSquareForm()

      this.updateLoadingState({ loading: true })
      this.recaptchaVerificationError = false

      // Initialize payment request but don't initialize Square form yet
      return this.loadSquarePayments()
        .then(_ => {
          return this.$store.dispatch('square/initPayment', {
            txn: this.txn,
            userid: this.userid,
            amount: this.amount,
            paymentOptions: {
              ...this.options,
              storeCard: this.storeCreditCard,
              card_present: false
            }
          })
        })
        .then(paymentRequest => {
          this.paymentRequest = {
            ...paymentRequest,
            verificationDetails: this.verificationDetails
          }

          this.$emit('connected', true)
          this.$store.commit('square/PAYMENT_DETAILS', this.paymentRequest)

          // Set default values but don't initialize Square form yet
          if (this.showCardSourceIDOverride) {
            this.showPaymentButtonVal = true
          }
          else {
            this.storeCreditCardSelection = (this.defaultUserCardStorage && this.enableUserCardStorage)
            this.selectedCard = (this.ignoreUserStoredCards && this.selectedCard === '') ? 'new' : this.selectedCard
          }

          // Only initialize Square form if "new card" is already selected
          if (this.selectedCard === 'new') {
            return this.initializeSquareCardForm()
            // Don't update loading state here - let initializeSquareCardForm do it after elements are ready
          }
          else {
            // No Square form to initialize, so we can set loading to false immediately
            this.updateLoadingState({ loading: false })
          }
        })
        .catch(err => {
          this.handleErrorResponse(err)
        })
    },
    usercardLoadingError: function(err) {
      this.paymentInitError = err
      this.handleErrorResponse(err)
    },
    errorModalClosed: function() {
      this.$store.commit('square/PAYMENT_INIT_ERROR')
    },
    completeModalClose: function(wasSuccessful) {
      // Check if this was a payment failure - if so, reinitialize the form for retry
      if (this.isPaymentFailure && !wasSuccessful) {
        // Clear the processing result and reset state for retry
        this.$store.commit('square/PROCESSING_RESULT')
        this.processing = false
        this.processingComplete = false
        this.squareFormLoaded = false
        this.squareFormInitialized = false

        // Reset selected card to 'new' to ensure form shows
        this.selectedCard = 'new'

        // Use nextTick to ensure the DOM is updated and modal is fully closed
        this.$nextTick(() => {
          // Small delay to ensure modal animation completes
          setTimeout(() => {
            // Reinitialize the payment form to allow re-entry
            this.init()
          }, 100)
        })
      }
      else {
        // For successful payments or non-retryable error cases, mark processing as complete
        // and hide the form elements
        this.processingComplete = true
        this.squareFormLoaded = false

        // Ensure the Square form is properly cleaned up
        this.cleanupSquareForm()

        this.$emit('completed', this.processingResult)
      }
    },
    handleErrorResponse: function(err) {
      this.updateLoadingState({ loading: false })
      this.$emit('error', err)
      this.processing = false
      this.squareFormLoaded = false
    },
    initializeSquareCardForm: function() {
      this.updateLoadingState({ loading: true })
      // Check if Square form is already initialized
      if (this.squareFormInitialized) {
        return Promise.resolve()
      }

      // Wait for card container to be in DOM before initializing
      return this.waitForCardContainer()
        .then(() => {
          return this.initializePaymentForm(this.cardContainerId)
        })
        .then(() => {
          this.squareFormInitialized = true
          this.squareFormLoaded = true
          this.showPaymentButtonVal = true
          console.log('Square form initialization completed - squareFormLoaded set to true')

          // Wait for Square to actually render the form elements
          return this.waitForSquareFormElements()
        })
        .then(() => {
          // Form elements are ready - immediately hide spinner and show form
          console.log('Square form elements ready - immediately updating loading state')
          this.updateLoadingState({ loading: false })
        })
        .catch(err => {
          console.error('Error initializing Square card form:', err)
          this.handleErrorResponse(err)
        })
    },

    waitForSquareFormElements: function() {
      return new Promise((resolve) => {
        let attempts = 0
        const maxAttempts = 50 // 5 seconds maximum wait

        // Wait for Square form elements to be rendered in the DOM
        const checkForSquareElements = () => {
          const container = document.querySelector('#square-card-container')
          if (container) {
            // Look for Square-specific elements that indicate the form is fully rendered
            const squareElements = container.querySelectorAll('iframe, input, div[id*="sq-"], div[class*="sq-"]')
            if (squareElements.length > 0) {
              console.log('Square form elements found, resolving immediately')
              resolve()
              return
            }
          }

          attempts++
          if (attempts >= maxAttempts) {
            console.log('Square form elements timeout - resolving anyway')
            resolve()
          }
          else {
            console.log(`Square form elements not yet rendered, waiting... (attempt ${attempts}/${maxAttempts})`)
            setTimeout(checkForSquareElements, 100)
          }
        }

        // Start checking immediately
        checkForSquareElements()
      })
    },

    waitForCardContainer: function() {
      return new Promise((resolve) => {
        const cardContainer = document.querySelector('#square-card-container')

        if (cardContainer) {
          // Element exists in DOM (even if hidden), resolve immediately
          resolve()
        }
        else {
          // Wait for element to be added to DOM
          const checkForContainer = () => {
            const container = document.querySelector('#square-card-container')
            if (container) {
              resolve()
            }
            else {
              // Retry after a short delay
              setTimeout(checkForContainer, 50)
            }
          }

          // Start checking after nextTick to ensure DOM is updated
          this.$nextTick(checkForContainer)
        }
      })
    },

    isElementVisible: function(element) {
      if (!element) return false
      const rect = element.getBoundingClientRect()
      const style = window.getComputedStyle(element)
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0'
      )
    },

    cleanupSquareForm: function() {
      // Call the parent mixin cleanup method
      if (this.$options.mixins) {
        const squareMixin = this.$options.mixins.find(mixin => mixin.methods && mixin.methods.cleanupSquareForm)
        if (squareMixin && squareMixin.methods.cleanupSquareForm) {
          squareMixin.methods.cleanupSquareForm.call(this)
        }
      }

      // Reset component state
      this.squareFormInitialized = false
      this.squareFormLoaded = false
      this.showPaymentButtonVal = false

      // Clean up any intersection observer
      if (this.cardContainerObserver) {
        this.cardContainerObserver.disconnect()
        this.cardContainerObserver = null
      }
    }
  }
}
</script>

<style lang="scss">
  .square-transaction-card-payment-wrapper {
    .store-user-credit-card {
      margin: 0 1.6rem;
      .custom-control-label {
        padding-left: 0.75rem;
      }
    }

    .payment-button-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      .amount {
        font-size: 1.1rem;
        font-weight: 600;
      }

      .surcharge-message {
        font-size: 0.875rem;
        margin-top: 0.25rem;
        opacity: 0.8;
        font-style: italic;
      }
    }

    // Fix Square form input heights
    #square-card-container {
      .sq-card-iframe-container {
        height: 47px !important; // Set normal input height
        min-height: 47px !important;
        max-height: 47px !important;
      }
    }
  }
</style>
